import {useEffect, useState} from 'react';
import {Navigate, useLocation, useNavigate} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {useDosPasos, useIngresar, useSalir, useSesionActual} from '../api/sesion';
import {SelectorDeIdioma} from '../componentes/selector-de-idioma';
import {tr} from '../i18n/idioma';

const clase = 'mt-1 w-full rounded-md border border-linea px-3 py-2 text-sm text-tinta';

// Pantalla de ingreso: correo y contrasena y, si la persona activo la
// verificacion en dos pasos, el codigo de su app de autenticacion.
export function PaginaIngresar() {
  const sesion = useSesionActual();
  const ingresar = useIngresar();
  const dosPasos = useDosPasos();
  const salir = useSalir();
  const navegar = useNavigate();
  const ubicacion = useLocation();
  const destino = (ubicacion.state as {desde?: string} | null)?.desde ?? '/';
  const [correo, setCorreo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [codigo, setCodigo] = useState('');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const pendiente = sesion.data?.dosPasosPendiente === true;

  useEffect(() => {
    document.title = tr('Ingresar · CNC Inventario', 'Sign in · CNC Inventory');
    return () => {
      document.title = tr('CNC Inventario', 'CNC Inventory');
    };
  }, []);

  if (sesion.data && !pendiente) return <Navigate to={destino} replace />;

  const entrar = async () => {
    setMensaje(null);
    try {
      const resultado = await ingresar.mutateAsync({correo: correo.trim().toLowerCase(), contrasena});
      if (resultado.estado === 'ok') void navegar(destino, {replace: true});
    } catch (error) {
      setMensaje(error instanceof ErrorDeApi ? error.message : tr('No se pudo entrar.', 'Could not sign in.'));
    }
  };

  const confirmarCodigo = async () => {
    setMensaje(null);
    try {
      await dosPasos.mutateAsync(codigo.trim());
      void navegar(destino, {replace: true});
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi
          ? error.message
          : tr('No se pudo comprobar el código.', 'Could not check the code.'),
      );
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-papel px-4">
      <div className="w-full max-w-sm rounded-xl border border-linea bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="marca text-xl font-semibold text-verde-oscuro">SUN VALLEY</div>
            <div className="mt-1 text-xs tracking-wide text-apagado uppercase">
              {tr('CNC · Inventario y exportaciones', 'CNC · Inventory and exports')}
            </div>
          </div>
          <SelectorDeIdioma />
        </div>
        {sesion.isPending ? (
          <p className="mt-6 text-sm text-apagado">{tr('Comprobando la sesión…', 'Checking your session…')}</p>
        ) : pendiente ? (
          <form
            className="mt-6 space-y-4"
            onSubmit={e => {
              e.preventDefault();
              void confirmarCodigo();
            }}
          >
            <p className="text-sm">
              {tr(
                'Escribe el código de 6 dígitos de tu app de autenticación (Microsoft Authenticator, Google Authenticator o similar).',
                'Enter the 6-digit code from your authenticator app (Microsoft Authenticator, Google Authenticator or similar).',
              )}
            </p>
            <label className="block text-xs text-apagado">
              {tr('Código', 'Code')}
              <input
                value={codigo}
                onChange={e => setCodigo(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                className={`${clase} text-center text-lg tracking-[0.4em]`}
              />
            </label>
            {mensaje && <p className="text-sm text-alerta">{mensaje}</p>}
            <button
              type="submit"
              disabled={dosPasos.isPending || codigo.trim().length !== 6}
              className="w-full rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {dosPasos.isPending ? tr('Comprobando…', 'Checking…') : tr('Continuar', 'Continue')}
            </button>
            <button
              type="button"
              onClick={() => void salir.mutateAsync()}
              className="w-full text-center text-xs text-apagado hover:text-tinta"
            >
              {tr('Entrar con otra cuenta', 'Sign in with another account')}
            </button>
          </form>
        ) : (
          <form
            className="mt-6 space-y-4"
            onSubmit={e => {
              e.preventDefault();
              void entrar();
            }}
          >
            <label className="block text-xs text-apagado">
              {tr('Correo', 'Email')}
              <input
                type="email"
                value={correo}
                onChange={e => setCorreo(e.target.value)}
                autoComplete="username"
                autoFocus
                required
                className={clase}
              />
            </label>
            <label className="block text-xs text-apagado">
              {tr('Contraseña', 'Password')}
              <input
                type="password"
                value={contrasena}
                onChange={e => setContrasena(e.target.value)}
                autoComplete="current-password"
                required
                className={clase}
              />
            </label>
            {mensaje && <p className="text-sm text-alerta">{mensaje}</p>}
            <button
              type="submit"
              disabled={ingresar.isPending}
              className="w-full rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {ingresar.isPending ? tr('Entrando…', 'Signing in…') : tr('Entrar', 'Sign in')}
            </button>
            <p className="text-xs text-apagado">
              {tr(
                'Si olvidaste tu contraseña, pídele a un administrador que la restablezca.',
                'If you forgot your password, ask an administrator to reset it.',
              )}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
